import { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import { z, ZodObject } from "zod";
import Account from "./Account";
import AutoComplete from "./AutoComplete";
import BudgetLines from "./BudgetLines";
import Checkbox from "./Checkbox";
import DateInput from "./DateInput";
import Dropdown from "./Dropdown";
import DropdownCheck from "./DropdownCheck";
import ExpenseTable from "./ExpenseTable";
import FileUpload from "./FileUpload";
import FileUploadSingle from "./FileUploadSingle";
import Input from "./Input";
import InputMask from "./InputMask";
import NumberInput from "./Number";
import RadioGroup from "./RadioGroup";
import RadioGroupDepending from "./RadioGroupDepending";
import Repeater from "./Repeater";
import Textarea from "./TextArea";
import ToggleSwitch from "./ToggleSwitch";
import {
    BudgetLinesElementType,
    BudgetLinesRow,
    ExpenseTableElementType,
    ExpenseTableRow,
    FileType,
    FormDataType,
    FormElementType,
    FormType,
    OptionType,
    RepeaterElementType,
    SectionType,
    TextblockElementType,
} from "../helpers/types";
import { getBrregInfo, formatNumber } from "../helpers/utils";

/**
 * Felles FormElement-renderer for både søknader (Form.tsx) og rapporter
 * (ReportForm.tsx). Den ene "saningen" for hvordan element-typer rendres.
 *
 * Skjermene wrapper sin egen sub-tre i `<FormElementContext.Provider>` med
 * sin egen provider sine data. RadioGroupDepending kaller FormElement
 * rekursivt — den finner kontekst-en via Provider-en uten å vite hvilken
 * skjerm-type den er i.
 */

export type FormElementContextValue = {
    formData: FormDataType;
    schema: ZodObject<any> | null;
    form: FormType;
    section: SectionType;
    errors: FormDataType;
    updateForm: (name: string, value: any, isFile?: boolean) => void;
    updateErrors: (name: string, value: any) => void;
    // Navigerer til skjemadelen der et felt ligger (brukt av expensetable sin
    // penn på forhåndsdefinerte rader). Kun satt i søknadsflyten (Form.tsx).
    navigateToField?: (fieldName: string) => void;
};

const FormElementContext = createContext<FormElementContextValue | null>(null);

export const FormElementProvider = FormElementContext.Provider;

export function useFormElementContext(): FormElementContextValue {
    const ctx = useContext(FormElementContext);
    if (!ctx) {
        throw new Error(
            "useFormElementContext må kalles innenfor en FormElementProvider — wrap renderingen i Form/ReportForm.tsx."
        );
    }
    return ctx;
}

/** Erstatter `{feltnavn}`-tokens i tekst med faktiske verdier fra formData. */
export const resolveRefs = (
    text: string | undefined,
    formData: Record<string, any>
): string => {
    if (!text?.includes("{")) return text ?? "";
    return text.replace(/\{([^}]+)\}/g, (_, name) => {
        const val = formData[name];
        if (val === undefined || val === null || val === "") return `{${name}}`;
        if (Array.isArray(val)) return val.join(", ");
        return String(val);
    });
};

export const FormElement = (props: FormElementType) => {
    const { type, name } = props as any;
    const { t } = useTranslation();
    const {
        formData,
        schema,
        form,
        section,
        errors,
        updateForm,
        updateErrors,
        navigateToField,
    } = useFormElementContext();
    const [errorType, setErrorType] = useState("");

    const resolvedProps: FormElementType = {
        ...props,
        label: resolveRefs((props as any).label, formData),
        ...((props as any).description !== undefined && {
            description: resolveRefs((props as any).description, formData),
        }),
    } as FormElementType;

    if (!form || !section) return null;

    const sectionSchema = schema?.shape[section.url];
    const formSchema = sectionSchema?.shape[form.url];
    if (!formSchema) return null;

    const inputSchema =
        formSchema._def?.schema?._def?.schema?.shape?.[name] ??
        formSchema._def?.schema?.shape?.[name] ??
        formSchema.shape?.[name];

    const handleInputChange = (value: string | number | boolean) => {
        updateForm(name, value);

        if (inputSchema) {
            const result = inputSchema.safeParse(value);
            if (!result.success) {
                updateErrors(
                    result.error.errors[0]?.path[0] || name,
                    result.error.errors[0]?.message || "Noe er feil"
                );
                setErrorType(result.error.errors[0].code);
            } else {
                updateErrors(name, null);
            }
        }

        // Re-valider budgetlines som har dette feltet som dependField.
        const budgetLinesDependents = form.elements.filter(
            (el) =>
                el.type === "budgetlines" &&
                (el as BudgetLinesElementType).dependField === name
        );
        if (budgetLinesDependents.length > 0) {
            const depVal =
                Number(String(value ?? "").replace(/\s+/g, "")) || 0;
            budgetLinesDependents.forEach((bl) => {
                const budgetEl = bl as BudgetLinesElementType;
                const rows: BudgetLinesRow[] = Array.isArray(formData[bl.name])
                    ? formData[bl.name]
                    : [];
                const total = rows.reduce(
                    (sum, row) =>
                        sum +
                        (Number(String(row.belop ?? "").replace(/\s+/g, "")) ||
                            0),
                    0
                );
                if (depVal > 0 && total !== depVal) {
                    updateErrors(
                        budgetEl.name,
                        "Summen i budsjettet stemmer ikke med totalkostnaden du har oppgitt. Du må sørge for at dette stemmer før du kan fortsette!"
                    );
                } else {
                    updateErrors(budgetEl.name, null);
                }
            });
        }

        // Re-valider expensetable når dette feltet enten er dependField (total-
        // referansen) ELLER en forhåndsdefinert-rad-referanse — begge endrer
        // totalsummen tabellen valideres mot.
        const expenseAffected = form.elements.filter(
            (el) =>
                el.type === "expensetable" &&
                (
                    (el as ExpenseTableElementType).dependField === name ||
                    ((el as ExpenseTableElementType).prefilledRows ?? []).some((p) => p.field === name)
                )
        );
        if (expenseAffected.length > 0) {
            const updatedFormData = { ...formData, [name]: value };
            expenseAffected.forEach((ed) => {
                const expenseEl = ed as ExpenseTableElementType;
                if (!expenseEl.dependField) {
                    updateErrors(expenseEl.name, null);
                    return;
                }
                const cols = (expenseEl.columns && expenseEl.columns.length > 0)
                    ? expenseEl.columns
                    : [{ name: "belop", label: "", type: "number" as const }];
                const totalCol =
                    expenseEl.totalColumn && cols.find((c) => c.name === expenseEl.totalColumn && c.type === "number")
                        ? expenseEl.totalColumn
                        : cols.find((c) => c.type === "number")?.name;
                if (!totalCol) {
                    updateErrors(expenseEl.name, null);
                    return;
                }
                const rows: ExpenseTableRow[] = Array.isArray(updatedFormData[expenseEl.name])
                    ? updatedFormData[expenseEl.name]
                    : [];
                // Forhåndsdefinerte rader teller med i totalsummen (verdi fra formData[field]).
                const prefilledTotal = (expenseEl.prefilledRows ?? []).reduce(
                    (sum, p) => sum + (Number(String(updatedFormData[p.field] ?? "").replace(/\s+/g, "")) || 0),
                    0
                );
                const total = prefilledTotal + rows.reduce(
                    (sum, row) => sum + (Number(String(row[totalCol] ?? "").replace(/\s+/g, "")) || 0),
                    0
                );
                const depVal = Number(String(updatedFormData[expenseEl.dependField] ?? "").replace(/\s+/g, "")) || 0;
                if (depVal > 0 && total !== depVal) {
                    updateErrors(
                        expenseEl.name,
                        `Summen i tabellen stemmer ikke med totalkostnaden du har oppgitt (NOK ${formatNumber(depVal)}). Du må sørge for at dette stemmer før du kan fortsette!!!`
                    );
                } else {
                    updateErrors(expenseEl.name, null);
                }
            });
        }

        // Kryss-validering for dependFieldPercent — kjør hele form-schema mot
        // oppdatert formData så avhengige felter blir revaludert riktig.
        const hasDependFieldPercentField = form.elements.filter(
            (element) => (element as any).rules?.dependFieldPercent
        );
        if (hasDependFieldPercentField.length > 0) {
            const updatedFormData = { ...formData, [name]: value };
            const result = formSchema.safeParse(updatedFormData);
            if (!result.success) {
                result.error.errors.map(
                    (error: {
                        path: string[];
                        message: string;
                        code: string;
                    }) => {
                        updateErrors(error.path[0], error.message);
                        setErrorType(error.code);
                    }
                );
            } else {
                hasDependFieldPercentField.map((element) => {
                    updateErrors((element as any).name, null);
                });
            }
        }
    };

    // -------- File helpers --------
    const handleFileDropSingle = (file: FileType) => {
        if (!file) return;
        updateForm(name, file);
        if (inputSchema) {
            const result = inputSchema.safeParse(file);
            if (!result.success) {
                updateErrors(
                    name,
                    result.error.errors[0]?.message || "Noe er feil"
                );
                setErrorType(result.error.errors[0].code);
            } else {
                updateErrors(name, null);
            }
        }
    };

    const removeFileSingle = () => {
        updateForm(name, null);
        if (inputSchema) {
            const result = inputSchema.safeParse(null);
            if (!result.success) {
                updateErrors(
                    name,
                    result.error.errors[0]?.message || "Noe er feil"
                );
                setErrorType(result.error.errors[0].code);
            } else {
                updateErrors(name, null);
            }
        }
    };

    const handleFileDropMultiple = (
        files: (FileType & { preview: string })[]
    ) => {
        if (!files || files.length === 0) return;
        const currentFiles = Array.isArray(formData[name])
            ? formData[name]
            : [];
        const updatedFiles = [
            ...currentFiles.filter(
                (currentFile: any) =>
                    !files.some((newFile) => newFile.name === currentFile.name)
            ),
            ...files,
        ];
        updateForm(name, updatedFiles);

        if (inputSchema) {
            const result = inputSchema.safeParse(files);
            if (!result.success) {
                updateErrors(
                    name,
                    result.error.errors[0]?.message || "Noe er feil"
                );
            } else {
                updateErrors(name, null);
            }
        }
    };

    const removeFileMultiple = (fileIndexToRemove: number) => {
        const currentFiles = Array.isArray(formData[name])
            ? formData[name]
            : [];
        const updatedFiles = currentFiles.filter(
            (_: any, index: number) => index !== fileIndexToRemove
        );
        updateForm(name, updatedFiles);

        if (inputSchema) {
            const result = inputSchema.safeParse(updatedFiles);
            if (!result.success) {
                updateErrors(
                    name,
                    result.error.errors[0]?.message || "Noe er feil"
                );
                setErrorType(result.error.errors[0].code);
            } else {
                updateErrors(name, null);
            }
        }
    };
    // -------- End file helpers --------

    switch (type) {
        case "brreg_required": {
            const handleBrregChange = async (value: string) => {
                handleInputChange(value);
                if (value.length === 9 && !isNaN(Number(value))) {
                    try {
                        const brregInfo = await getBrregInfo(value);
                        updateForm("name", brregInfo.navn || "");
                        updateForm(
                            "municipality",
                            brregInfo.postadresse?.kommune
                                ?? brregInfo.forretningsadresse?.kommune
                                ?? brregInfo.beliggenhetsadresse?.kommune // underenheter har bare denne
                                ?? t("input_brreg_error")
                        );
                        updateForm("brreg", brregInfo);
                    } catch (err: any) {
                        console.error(err);
                    }
                } else {
                    updateForm("name", "");
                    updateForm("municipality", "");
                }
            };
            return (
                <InputMask
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleBrregChange}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );
        }

        case "org_role": {
            const roles = formData["brreg"]?.roles ?? null;
            const filteredRoles =
                roles?.rollegrupper
                    ?.map((r: any) =>
                        r.roller && r.roller.length
                            ? r.roller.map(
                                  (rr: any) => rr?.type?.beskrivelse
                              )
                            : []
                    )
                    .flat() ?? [];
            const options = filteredRoles
                ?.filter(
                    (item: string, index: number, self: string[]) =>
                        index === self.findIndex((s) => s === item)
                )
                .map((role: string) => ({ label: role, value: role }));
            return (
                <AutoComplete
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    debug={formData["brreg"] ? formData["brreg"] : null}
                    options={options}
                    {...(resolvedProps as any)}
                />
            );
        }

        case "text":
            return (
                <Input
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "textarea":
            return (
                <Textarea
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "numberformat":
            return (
                <NumberInput
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    errorType={errorType}
                    {...(resolvedProps as any)}
                />
            );

        case "dropdown":
            return (
                <Dropdown
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "dropdowncheck":
            return (
                <DropdownCheck
                    key={name}
                    value={
                        Array.isArray(formData[name])
                            ? (formData[name] as string[])
                            : []
                    }
                    onChange={(value: string[]) => {
                        updateForm(name, value);
                        if (inputSchema) {
                            const result = inputSchema.safeParse(value);
                            if (!result.success) {
                                updateErrors(
                                    name,
                                    result.error.errors[0]?.message ||
                                        "Noe er feil"
                                );
                            } else {
                                updateErrors(name, null);
                            }
                        }
                    }}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "radiogroupdepending":
            return (
                <RadioGroupDepending
                    key={name}
                    checked={formData[name] ?? ""}
                    onCheck={handleInputChange}
                    updateForm={updateForm}
                    formData={formData}
                    {...(resolvedProps as any)}
                />
            );

        case "inputmask":
            return (
                <InputMask
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "fileuploadsingle":
            return (
                <FileUploadSingle
                    key={name}
                    onHandleDrop={handleFileDropSingle}
                    onRemoveFile={removeFileSingle}
                    file={formData[name] ?? null}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "fileupload":
            return (
                <FileUpload
                    onHandleDrop={handleFileDropMultiple}
                    onRemoveFile={(file: number) => removeFileMultiple(file)}
                    files={formData[name] ?? null}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "account":
            return (
                <Account
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={handleInputChange}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "checkbox": {
            const p = resolvedProps as any;
            if (
                p.hide_if &&
                (!formData[p.hide_if.name] ||
                    formData[p.hide_if.name] === p.hide_if.value)
            ) {
                return null;
            }
            return (
                <Checkbox
                    key={name}
                    value={formData[name] ?? ""}
                    onChange={(value: any) => handleInputChange(value)}
                    error={errors[name]}
                    {...p}
                />
            );
        }

        case "ToggleSwitch":
            return (
                <ToggleSwitch
                    value={formData[name] ?? ""}
                    onCheck={(value: any) => handleInputChange(value)}
                    {...(resolvedProps as any)}
                />
            );

        case "date":
            return (
                <DateInput
                    value={formData[name] ?? ""}
                    onChange={(value: any) => handleInputChange(value)}
                    error={errors[name]}
                    {...(resolvedProps as any)}
                />
            );

        case "RadioGroup":
        case "bigradio": {
            // Vis melding-boksen (option.description) når et alternativ som har
            // den velges — samme oppførsel som PreQuestion, nå også i skjemaet.
            const alternatives: OptionType[] = (props as any).alternatives ?? [];
            const selectedAlt = alternatives.find(
                (a) => String(a.value) === String(formData[name])
            );
            return (
                <>
                    <RadioGroup
                        items={alternatives}
                        onCheck={(value: any) => updateForm(name, value)}
                        checked={formData[name] ?? null}
                        classes="justify-content-start"
                        {...(resolvedProps as any)}
                    />
                    {selectedAlt?.description?.content && (
                        <div
                            className={`alert alert-${selectedAlt.description.type} text-start mt-3`}
                            role="alert"
                        >
                            {parse(selectedAlt.description.content)}
                        </div>
                    )}
                </>
            );
        }

        case "budgetlines": {
            const budgetEl = resolvedProps as BudgetLinesElementType;
            const emptyBudgetRow = (): BudgetLinesRow => {
                const row: BudgetLinesRow = { belop: "" };
                budgetEl.columns.forEach((col) => {
                    row[col.name] = "";
                });
                return row;
            };
            const rows: BudgetLinesRow[] =
                formData[name] === undefined
                    ? [emptyBudgetRow()]
                    : Array.isArray(formData[name])
                    ? formData[name]
                    : [];

            const handleBudgetChange = (newRows: BudgetLinesRow[]) => {
                updateForm(name, newRows);
                if (budgetEl.dependField) {
                    const depVal =
                        Number(
                            String(
                                formData[budgetEl.dependField] ?? ""
                            ).replace(/\s+/g, "")
                        ) || 0;
                    const total = newRows.reduce(
                        (sum, row) =>
                            sum +
                            (Number(
                                String(row.belop ?? "").replace(/\s+/g, "")
                            ) || 0),
                        0
                    );
                    if (depVal > 0 && total !== depVal) {
                        updateErrors(
                            name,
                            "Summen i budsjettet stemmer ikke med totalkostnaden du har oppgitt. Du må sørge for at dette stemmer før du kan fortsette!"
                        );
                    } else {
                        updateErrors(name, null);
                    }
                } else {
                    updateErrors(name, null);
                }
            };

            return (
                <BudgetLines
                    key={name}
                    {...budgetEl}
                    value={rows}
                    onChange={handleBudgetChange}
                    error={errors[name]}
                />
            );
        }

        case "repeater": {
            const repeaterEl = resolvedProps as RepeaterElementType;
            const emptyRepeaterRow = (): BudgetLinesRow => {
                const row: BudgetLinesRow = {};
                repeaterEl.columns.forEach((col) => {
                    row[col.name] = "";
                });
                return row;
            };
            const rows: BudgetLinesRow[] =
                formData[name] === undefined
                    ? [emptyRepeaterRow()]
                    : Array.isArray(formData[name])
                    ? formData[name]
                    : [];
            return (
                <Repeater
                    key={name}
                    {...repeaterEl}
                    value={rows}
                    onChange={(newRows) => {
                        updateForm(name, newRows);
                        updateErrors(name, null);
                    }}
                    error={errors[name]}
                />
            );
        }

        case "expensetable": {
            const expenseEl = resolvedProps as ExpenseTableElementType;

            // Kolonner med fallback til standardoppsettet (matcher ExpenseTable.tsx).
            const expenseCols = (expenseEl.columns && expenseEl.columns.length > 0)
                ? expenseEl.columns
                : [
                    { name: "beskrivelse", label: "Beskrivelse", type: "text" as const },
                    { name: "belop",       label: "Beløp (kr)",  type: "number" as const },
                    { name: "bilag",       label: "Bilag",       type: "attachment" as const },
                ];

            const emptyExpenseRow = (): ExpenseTableRow => {
                const row: ExpenseTableRow = {};
                expenseCols.forEach((col) => { row[col.name] = col.type === "attachment" ? null : ""; });
                return row;
            };

            // Alltid minst én rad når tom — på lik linje med budgetlines.
            const rows: ExpenseTableRow[] =
                formData[name] === undefined
                    ? [emptyExpenseRow()]
                    : Array.isArray(formData[name])
                    ? (formData[name] as ExpenseTableRow[])
                    : [];

            // Total-kolonne: eksplisitt totalColumn (hvis number), ellers første number-kolonne.
            const totalCol =
                expenseEl.totalColumn && expenseCols.find((c) => c.name === expenseEl.totalColumn && c.type === "number")
                    ? expenseEl.totalColumn
                    : expenseCols.find((c) => c.type === "number")?.name;

            // Forhåndsdefinerte (låste) rader — verdi hentes live fra formData[field].
            const prefilledResolved = (expenseEl.prefilledRows ?? []).map((p) => ({
                field: p.field,
                label: p.label,
                value: (formData[p.field] as string | number | undefined) ?? "",
            }));
            const prefilledTotal = totalCol
                ? prefilledResolved.reduce(
                      (sum, p) => sum + (Number(String(p.value ?? "").replace(/\s+/g, "")) || 0),
                      0
                  )
                : 0;

            const handleExpenseChange = (newRows: ExpenseTableRow[]) => {
                updateForm(name, newRows);
                if (expenseEl.dependField && totalCol) {
                    const depVal = Number(String(formData[expenseEl.dependField] ?? "").replace(/\s+/g, "")) || 0;
                    // Totalsummen inkluderer både forhåndsdefinerte og bruker-rader.
                    const total =
                        prefilledTotal +
                        newRows.reduce(
                            (sum, row) => sum + (Number(String(row[totalCol] ?? "").replace(/\s+/g, "")) || 0),
                            0
                        );
                    if (depVal > 0 && total !== depVal) {
                        updateErrors(name, `Summen i tabellen stemmer ikke med totalkostnaden du har oppgitt (NOK ${formatNumber(depVal)}). Du må sørge for at dette stemmer før du kan fortsette!`);
                    } else {
                        updateErrors(name, null);
                    }
                } else {
                    updateErrors(name, null);
                }
            };

            return (
                <ExpenseTable
                    key={name}
                    {...expenseEl}
                    value={rows}
                    prefilledRows={prefilledResolved}
                    onEditPrefilled={navigateToField}
                    onChange={handleExpenseChange}
                    error={errors[name]}
                />
            );
        }

        case "hidden":
            return null;

        // Tekstblokk: innhold, ikke et felt. Ingen value/onChange, ingen
        // validering — se `textblock`-hoppet i generateValidationSchema.
        case "textblock": {
            const { content, label } = resolvedProps as TextblockElementType;
            return (
                <div key={name} className="mb-3">
                    {label && <h5 className="mb-2">{label}</h5>}
                    {content && <div>{parse(content)}</div>}
                </div>
            );
        }
    }

    return <p>{type}</p>;
};

// Sørg for at z er importert som type-only-konsumert (unngår tree-shake-fjerning
// hvis kompilatoren skulle fjerne den feilaktig). Eksporten brukes ikke utad.
export type _FormElementSchemaShape = ZodObject<any> | typeof z;
